import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL + '/internal-chat/';
const HELPDESK_BASE_URL = process.env.REACT_APP_API_BASE_URL + '/api/helpdesk';

const helpdeskEndpoint = (path) => `${HELPDESK_BASE_URL}${path}`;

const buildAuthHeaders = (token, extraHeaders = {}) => ({
    Authorization: `Bearer ${token}`,
    ...extraHeaders,
});

const parseApiError = async(response, fallbackMessage) => {
    try {
        const data = await response.json();
        if (data ?.error ?.message) {
            return data.error.message;
        }
        if (Array.isArray(data ?.non_field_errors) && data.non_field_errors.length) {
            return data.non_field_errors.join(', ');
        }
        if (typeof data === 'object' && data !== null) {
            const firstField = Object.keys(data)[0];
            const firstValue = data[firstField];
            if (Array.isArray(firstValue) && firstValue.length) {
                return String(firstValue[0]);
            }
        }
        if (data ?.message) {
            return data.message;
        }
        return fallbackMessage;
    } catch (error) {
        return fallbackMessage;
    }
};

// Get conversation for current user
export const getConversation = createAsyncThunk(
    'internalChat/getConversation',
    async(params = {}, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            let url = `${API_BASE_URL}conversation/`;

            // For employees, add brand_id query parameter if provided
            if (params.brand_id && user.user_type === 'employee') {
                url += `?brand_id=${params.brand_id}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                if (response.status === 404) {
                    // No conversation exists yet, return empty state
                    return { conversation: null, messages: [] };
                }
                { const e = new Error(`HTTP ${response.status}`); e.status = response.status; throw e; }
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Get messages with pagination
export const getMessages = createAsyncThunk(
    'internalChat/getMessages',
    async(params = {}, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            const searchParams = new URLSearchParams();
            if (params.brand_id && user.user_type === 'employee') {
                searchParams.append('brand_id', params.brand_id);
            }
            if (params.page) {
                searchParams.append('page', params.page);
            }
            if (params.page_size) {
                searchParams.append('page_size', params.page_size);
            }

            const url = `${API_BASE_URL}messages/?${searchParams.toString()}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                { const e = new Error(`HTTP ${response.status}`); e.status = response.status; throw e; }
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Send message
export const sendMessage = createAsyncThunk(
    'internalChat/sendMessage',
    async(messageData, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${API_BASE_URL}messages/send/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(messageData),
            });

            if (!response.ok) {
                if (response.status === 429) {
                    throw new Error('Rate limit exceeded. Please wait before sending another message.');
                }
                throw new Error(`Failed to send message: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Mark messages as read
export const markMessagesAsRead = createAsyncThunk(
    'internalChat/markMessagesAsRead',
    async(params = {}, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            const body = {};
            if (params.brand_id && user.user_type === 'employee') {
                body.brand_id = params.brand_id;
            }

            const response = await fetch(`${API_BASE_URL}messages/mark-read/`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                throw new Error(`Failed to mark messages as read: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Get unread count
export const getUnreadCount = createAsyncThunk(
    'internalChat/getUnreadCount',
    async(params = {}, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            let url = `${API_BASE_URL}unread-count/`;

            if (params.brand_id && user.user_type === 'employee') {
                url += `?brand_id=${params.brand_id}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                { const e = new Error(`HTTP ${response.status}`); e.status = response.status; throw e; }
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Get unread counts for all brands (for employees only)
export const getAllBrandUnreadCounts = createAsyncThunk(
    'internalChat/getAllBrandUnreadCounts',
    async(_, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            if (user.user_type !== 'employee') {
                throw new Error('Only employees can fetch all brand unread counts');
            }

            const response = await fetch(`${API_BASE_URL}unread-counts/all-brands/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                { const e = new Error(`HTTP ${response.status}`); e.status = response.status; throw e; }
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Create conversation
export const createConversation = createAsyncThunk(
    'internalChat/createConversation',
    async(conversationData, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${API_BASE_URL}conversation/create/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(conversationData),
            });

            if (!response.ok) {
                throw new Error(`Failed to create conversation: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const getHelpdeskTickets = createAsyncThunk(
    'internalChat/getHelpdeskTickets',
    async(params = {}, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            const query = new URLSearchParams();
            if (params.status) query.append('status', params.status);
            if (params.page) query.append('page', String(params.page));
            if (params.per_page) query.append('per_page', String(params.per_page));

            const suffix = query.toString() ? `?${query.toString()}` : '';
            const response = await fetch(helpdeskEndpoint(`/tickets/${suffix}`), {
                method: 'GET',
                headers: buildAuthHeaders(token, {
                    'Content-Type': 'application/json',
                }),
            });

            if (!response.ok) {
                const errorMessage = await parseApiError(response, `Failed to fetch tickets: ${response.status}`);
                throw new Error(errorMessage);
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const getHelpdeskTicketDetail = createAsyncThunk(
    'internalChat/getHelpdeskTicketDetail',
    async(ticketId, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(helpdeskEndpoint(`/tickets/${ticketId}/`), {
                method: 'GET',
                headers: buildAuthHeaders(token, {
                    'Content-Type': 'application/json',
                }),
            });

            if (!response.ok) {
                const errorMessage = await parseApiError(response, `Failed to fetch ticket detail: ${response.status}`);
                throw new Error(errorMessage);
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const getHelpdeskCategories = createAsyncThunk(
    'internalChat/getHelpdeskCategories',
    async(_, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;
            if (!token) throw new Error('No authentication token found');
            const response = await fetch(helpdeskEndpoint('/categories/'), {
                headers: buildAuthHeaders(token),
            });
            if (!response.ok) { const e = new Error(`HTTP ${response.status}`); e.status = response.status; throw e; }
            const data = await response.json();
            return data.categories || [];
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const createHelpdeskTicket = createAsyncThunk(
    'internalChat/createHelpdeskTicket',
    async(payload, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            const formData = new FormData();
            formData.append('title', payload.title);
            formData.append('description', payload.description);
            formData.append('category', payload.category);
            formData.append('urgency', payload.urgency);
            formData.append('source', payload.source || 'console');
            formData.append('book_call', String(Boolean(payload.book_call)));

            if (Array.isArray(payload.attachments)) {
                payload.attachments.forEach((file) => {
                    formData.append('attachments', file);
                });
            }

            const response = await fetch(helpdeskEndpoint('/tickets/'), {
                method: 'POST',
                headers: buildAuthHeaders(token),
                body: formData,
            });

            if (!response.ok) {
                const errorMessage = await parseApiError(response, `Failed to create ticket: ${response.status}`);
                throw new Error(errorMessage);
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const sendHelpdeskMessage = createAsyncThunk(
    'internalChat/sendHelpdeskMessage',
    async({ ticketId, content, attachments = [] }, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            let response;

            if (attachments.length > 0) {
                const formData = new FormData();
                formData.append('content', content);
                attachments.forEach((file) => {
                    formData.append('attachments', file);
                });

                response = await fetch(helpdeskEndpoint(`/tickets/${ticketId}/messages/`), {
                    method: 'POST',
                    headers: buildAuthHeaders(token),
                    body: formData,
                });
            } else {
                response = await fetch(helpdeskEndpoint(`/tickets/${ticketId}/messages/`), {
                    method: 'POST',
                    headers: buildAuthHeaders(token, {
                        'Content-Type': 'application/json',
                    }),
                    body: JSON.stringify({ content }),
                });
            }

            if (!response.ok) {
                const errorMessage = await parseApiError(response, `Failed to send message: ${response.status}`);
                throw new Error(errorMessage);
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const escalateHelpdeskTicket = createAsyncThunk(
    'internalChat/escalateHelpdeskTicket',
    async(ticketId, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(helpdeskEndpoint(`/tickets/${ticketId}/escalate/`), {
                method: 'POST',
                headers: buildAuthHeaders(token, {
                    'Content-Type': 'application/json',
                }),
            });

            if (!response.ok) {
                const errorMessage = await parseApiError(response, `Failed to escalate ticket: ${response.status}`);
                throw new Error(errorMessage);
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const reopenHelpdeskTicket = createAsyncThunk(
    'internalChat/reopenHelpdeskTicket',
    async({ ticketId, context }, { getState, rejectWithValue }) => {
        try {
            const { user } = getState();
            const token = user.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(helpdeskEndpoint(`/tickets/${ticketId}/reopen/`), {
                method: 'POST',
                headers: buildAuthHeaders(token, {
                    'Content-Type': 'application/json',
                }),
                body: JSON.stringify({ context }),
            });

            if (!response.ok) {
                const errorMessage = await parseApiError(response, `Failed to reopen ticket: ${response.status}`);
                throw new Error(errorMessage);
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const internalChatSlice = createSlice({
    name: 'internalChat',
    initialState: {
        conversation: null,
        messages: [],
        loading: false,
        error: null,
        unreadCount: 0,
        brandUnreadCounts: {}, // Object mapping brand_id -> unread_count
        sendingMessage: false,
        hasNextPage: true,
        currentPage: 1,
        tickets: [],
        categories: [],
        categoriesLoading: false,
        selectedTicketId: null,
        ticketDetail: null,
        ticketsLoading: false,
        ticketDetailLoading: false,
        ticketActionLoading: false,
        creatingTicket: false,
        ticketsPagination: {
            total: 0,
            page: 1,
            per_page: 20,
        },
        ticketSuccessMessage: null,
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearTicketSuccessMessage: (state) => {
            state.ticketSuccessMessage = null;
        },
        setSelectedTicketId: (state, action) => {
            state.selectedTicketId = action.payload;
            state.ticketDetail = null;
            state.ticketDetailLoading = false;
        },
        resetChat: (state) => {
            state.conversation = null;
            state.messages = [];
            state.error = null;
            state.unreadCount = 0;
            state.currentPage = 1;
            state.hasNextPage = true;
            state.tickets = [];
            state.selectedTicketId = null;
            state.ticketDetail = null;
            state.ticketsLoading = false;
            state.ticketDetailLoading = false;
            state.ticketActionLoading = false;
            state.creatingTicket = false;
            state.ticketsPagination = {
                total: 0,
                page: 1,
                per_page: 20,
            };
            state.ticketSuccessMessage = null;
        },
        // Optimistically add message while sending
        addOptimisticMessage: (state, action) => {
            const tempMessage = {
                id: `temp-${Date.now()}`,
                content: action.payload.content,
                sender_type: action.payload.sender_type,
                sender_name: action.payload.sender_name,
                sent_at: new Date().toISOString(),
                is_read_by_employee: false,
                is_read_by_brand: false,
                isOptimistic: true,
            };
            state.messages.push(tempMessage);
        },
        // Replace optimistic message with real one
        replaceOptimisticMessage: (state, action) => {
            const { tempId, realMessage } = action.payload;
            const index = state.messages.findIndex(msg => msg.id === tempId);
            if (index !== -1) {
                state.messages[index] = {...realMessage, isOptimistic: false };
            }
        },
    },
    extraReducers: (builder) => {
        builder
        // Get conversation
            .addCase(getConversation.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getConversation.fulfilled, (state, action) => {
                state.loading = false;
                state.conversation = action.payload.conversation;
                state.messages = action.payload.messages || [];
            })
            .addCase(getConversation.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

        // Get messages
        .addCase(getMessages.pending, (state) => {
                if (state.currentPage === 1) {
                    state.loading = true;
                }
            })
            .addCase(getMessages.fulfilled, (state, action) => {
                state.loading = false;
                const { results, next } = action.payload;

                if (state.currentPage === 1) {
                    state.messages = results || [];
                } else {
                    // Append for pagination
                    state.messages = [...(results || []), ...state.messages];
                }

                state.hasNextPage = !!next;
                state.currentPage += 1;
            })
            .addCase(getMessages.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

        // Send message
        .addCase(sendMessage.pending, (state) => {
                state.sendingMessage = true;
                state.error = null;
            })
            .addCase(sendMessage.fulfilled, (state, action) => {
                state.sendingMessage = false;
                const { message, conversation_id, created } = action.payload;

                // Remove optimistic message if it exists
                const optimisticIndex = state.messages.findIndex(msg => msg.isOptimistic);
                if (optimisticIndex !== -1) {
                    state.messages.splice(optimisticIndex, 1);
                }

                // Add the real message
                state.messages.push(message);

                // Update conversation if it was created
                if (created && conversation_id) {
                    if (!state.conversation) {
                        state.conversation = { id: conversation_id };
                    }
                }
            })
            .addCase(sendMessage.rejected, (state, action) => {
                state.sendingMessage = false;
                state.error = action.payload;

                // Remove optimistic message on error
                state.messages = state.messages.filter(msg => !msg.isOptimistic);
            })

        // Mark as read
        .addCase(markMessagesAsRead.fulfilled, (state) => {
            // Update local messages to mark them as read
            state.messages = state.messages.map(msg => ({
                ...msg,
                is_read_by_brand: true,
                is_read_by_employee: true,
            }));
            state.unreadCount = 0;
        })

        // Unread count
        .addCase(getUnreadCount.fulfilled, (state, action) => {
            state.unreadCount = action.payload.unread_count || 0;
        })

        // Get all brand unread counts
        .addCase(getAllBrandUnreadCounts.fulfilled, (state, action) => {
                // action.payload should be an object like { brand_id: unread_count, ... }
                state.brandUnreadCounts = action.payload;
            })
            .addCase(getAllBrandUnreadCounts.rejected, (state, action) => {
                console.error('Failed to fetch brand unread counts:', action.payload);
            })

        // Create conversation
        .addCase(createConversation.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createConversation.fulfilled, (state, action) => {
                state.loading = false;
                const { message, conversation_id } = action.payload;

                // Set the conversation
                state.conversation = { id: conversation_id };

                // Add the initial message
                state.messages = [message];
            })
            .addCase(createConversation.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

        // Helpdesk tickets list
        .addCase(getHelpdeskTickets.pending, (state) => {
                state.ticketsLoading = true;
                state.error = null;
            })
            .addCase(getHelpdeskTickets.fulfilled, (state, action) => {
                state.ticketsLoading = false;
                state.tickets = action.payload.tickets || [];
                state.ticketsPagination = {
                    total: action.payload.total || 0,
                    page: action.payload.page || 1,
                    per_page: action.payload.per_page || 20,
                };

                const selectedStillExists = state.tickets.some(
                    (ticket) => (ticket.ticket_id || ticket.id) === state.selectedTicketId
                );

                if ((!state.selectedTicketId || !selectedStillExists) && state.tickets.length > 0) {
                    const firstTicket = state.tickets[0];
                    state.selectedTicketId = firstTicket.ticket_id || firstTicket.id;
                } else if (state.tickets.length === 0) {
                    state.selectedTicketId = null;
                    state.ticketDetail = null;
                }
            })
            .addCase(getHelpdeskTickets.rejected, (state, action) => {
                state.ticketsLoading = false;
                state.error = action.payload;
            })

        // Helpdesk ticket detail
        .addCase(getHelpdeskTicketDetail.pending, (state) => {
                // Only show loading spinner on fresh load; silent for background polls
                if (!state.ticketDetail) {
                    state.ticketDetailLoading = true;
                }
                state.error = null;
            })
            .addCase(getHelpdeskTicketDetail.fulfilled, (state, action) => {
                state.ticketDetailLoading = false;
                const payload = action.payload || {};

                if (payload.ticket) {
                    state.ticketDetail = payload;
                } else {
                    state.ticketDetail = {
                        ticket: payload,
                        messages: payload.messages || [],
                        attachments: payload.attachments || [],
                        can_escalate: payload.can_escalate,
                        sla_text: payload.sla_text,
                        calendly_url: (payload.category_detail && payload.category_detail.calendly_url) || null,
                    };
                }
            })
            .addCase(getHelpdeskTicketDetail.rejected, (state, action) => {
                state.ticketDetailLoading = false;
                state.error = action.payload;
            })

        // Helpdesk categories
        .addCase(getHelpdeskCategories.pending, (state) => {
                state.categoriesLoading = true;
            })
            .addCase(getHelpdeskCategories.fulfilled, (state, action) => {
                state.categoriesLoading = false;
                state.categories = action.payload;
            })
            .addCase(getHelpdeskCategories.rejected, (state) => {
                state.categoriesLoading = false;
            })

        // Create helpdesk ticket
        .addCase(createHelpdeskTicket.pending, (state) => {
                state.creatingTicket = true;
                state.error = null;
            })
            .addCase(createHelpdeskTicket.fulfilled, (state, action) => {
                state.creatingTicket = false;
                state.ticketSuccessMessage = 'Ticket submitted successfully. Typical response time: within 24 hours.';

                const newTicketId = action.payload.ticket_id;
                if (newTicketId) {
                    state.selectedTicketId = newTicketId;
                }
            })
            .addCase(createHelpdeskTicket.rejected, (state, action) => {
                state.creatingTicket = false;
                state.error = action.payload;
            })

        // Helpdesk send message
        .addCase(sendHelpdeskMessage.pending, (state) => {
                state.ticketActionLoading = true;
                state.error = null;
            })
            .addCase(sendHelpdeskMessage.fulfilled, (state) => {
                state.ticketActionLoading = false;
            })
            .addCase(sendHelpdeskMessage.rejected, (state, action) => {
                state.ticketActionLoading = false;
                state.error = action.payload;
            })

        // Helpdesk escalate
        .addCase(escalateHelpdeskTicket.pending, (state) => {
                state.ticketActionLoading = true;
                state.error = null;
            })
            .addCase(escalateHelpdeskTicket.fulfilled, (state) => {
                state.ticketActionLoading = false;
                state.ticketSuccessMessage = 'Ticket escalated successfully.';
            })
            .addCase(escalateHelpdeskTicket.rejected, (state, action) => {
                state.ticketActionLoading = false;
                state.error = action.payload;
            })

        // Helpdesk reopen
        .addCase(reopenHelpdeskTicket.pending, (state) => {
                state.ticketActionLoading = true;
                state.error = null;
            })
            .addCase(reopenHelpdeskTicket.fulfilled, (state) => {
                state.ticketActionLoading = false;
                state.ticketSuccessMessage = 'Ticket reopened successfully.';
            })
            .addCase(reopenHelpdeskTicket.rejected, (state, action) => {
                state.ticketActionLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearError,
    clearTicketSuccessMessage,
    resetChat,
    setSelectedTicketId,
    addOptimisticMessage,
    replaceOptimisticMessage
} = internalChatSlice.actions;

export default internalChatSlice.reducer;